import { useEffect } from "react";
import { Link } from "wouter";
import { Building2, Clock, Shield, Check, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { OpenGraphTags } from "@/components/OpenGraphTags";
import { trackCTAClick } from "@/lib/analytics";
import FAQSchema, { commercialPaintingFAQs } from "@/components/FAQSchema";
import FAQAccordion from "@/components/FAQAccordion";
import { CanonicalTag } from "@/components/CanonicalTag";

export default function CommercialPaintingService() {
  const canonicalUrl = "https://danispainting.net/commercial-painting";
  
  useEffect(() => {
    document.title = "Commercial Painting Services Santa Barbara & Ventura | Danis Painting Co.";
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbItems = [
    { label: "Home", url: "/" },
    { label: "Services", url: "/services" },
    { label: "Commercial Painting", url: "" },
  ];

  const serviceTypes = [
    {
      title: "Office Buildings",
      description: "Professional painting for corporate offices, professional suites, and business complexes. We work around your schedule to minimize disruption.",
      icon: Building2,
    },
    {
      title: "Retail Storefronts",
      description: "Eye-catching finishes that attract customers and enhance your brand image. Fast turnaround to keep your business operational.",
      icon: Building2,
    },
    {
      title: "Restaurants & Hospitality",
      description: "Durable, easy-to-clean finishes for high-traffic dining spaces. Low-VOC paints ensure safe indoor air quality for staff and guests.",
      icon: Building2,
    },
    {
      title: "Warehouses & Industrial",
      description: "Heavy-duty coatings for warehouses, manufacturing facilities, and industrial spaces. OSHA-compliant safety standards.",
      icon: Building2,
    },
    {
      title: "Medical Facilities",
      description: "Antimicrobial coatings and low-VOC paints for healthcare environments. Strict adherence to health and safety regulations.",
      icon: Building2,
    },
    {
      title: "Multi-Unit Residential",
      description: "Apartment complexes, condos, and student housing. Efficient scheduling and durable finishes that withstand heavy use.",
      icon: Building2,
    },
  ];

  const whyChooseUs = [
    {
      title: "30+ Years Experience",
      description: "Three decades of serving Santa Barbara and Ventura's commercial community with exceptional results.",
    },
    {
      title: "Licensed & Insured",
      description: "Fully licensed (CSLB), insured, and OSHA-trained. Comprehensive liability and workers' compensation coverage.",
    },
    {
      title: "Flexible Scheduling",
      description: "Evening, weekend, and off-hours work available to minimize business disruption and maintain operations.",
    },
    {
      title: "Premium Materials",
      description: "Commercial-grade paints with low-VOC formulas. Marine-grade coatings for coastal properties.",
    },
    {
      title: "Detailed Estimates",
      description: "Transparent pricing with detailed scope-of-work documents. No hidden fees or surprise charges.",
    },
    {
      title: "Quality Guarantee",
      description: "We stand behind our work with comprehensive warranties. Your satisfaction is our priority.",
    },
  ];

  const locationArticles = [
    {
      city: "Goleta",
      title: "Top 5 Commercial Painting Mistakes Goleta Business Owners Make",
      slug: "goleta-commercial-painting-guide",
      excerpt: "UCSB student housing, Camino Real Marketplace, and Airport district commercial painting strategies.",
    },
    {
      city: "Santa Barbara",
      title: "Santa Barbara Commercial Painting: Downtown, State Street & Funk Zone Guide",
      slug: "santa-barbara-commercial-painting",
      excerpt: "Historic preservation, State Street design standards, and Funk Zone restaurant transformations.",
    },
    {
      city: "Carpinteria",
      title: "Coastal Commercial Property Painting: The Complete Carpinteria Business Guide",
      slug: "carpinteria-commercial-painting",
      excerpt: "Beachfront businesses, Linden Avenue retail, and marine-grade coastal protection strategies.",
    },
    {
      city: "Ventura",
      title: "Ventura Harbor & Main Street Business Painting Guide",
      slug: "ventura-commercial-painting",
      excerpt: "Harbor Village waterfront properties, Main Street historic buildings, and tourism-focused scheduling.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url={canonicalUrl} />
      <OpenGraphTags
        title="Commercial Painting Services Santa Barbara & Ventura | Danis Painting Co."
        description="Professional commercial painting for offices, retail, restaurants, and warehouses. 30+ years serving Santa Barbara & Ventura. Licensed, insured, flexible scheduling. Free estimates: 805-403-8727"
        url="https://danispainting.net/service/commercial-painting"
        image="https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/gallery-7_a305a628_448adb01.webp"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-36 md:pb-20 text-white overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #0a1628 100%)' }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: 'url(https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/hero-bg_5849f6b9_e05061fa.webp)' }}></div>
        <div className="container px-4 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Commercial Painting Services</h1>
            <p className="text-xl text-white/90 mb-8">
              Transform your business space with professional commercial painting services. Serving Santa Barbara, Ventura, and Los Angeles with over 30 years of experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-[#4caf30] hover:bg-[#3d9426] text-white"
                onClick={() => {
                  trackCTAClick('call_now', 'hero');
                  window.location.href = "tel:805-403-8727";
                }}
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Now: 805-403-8727
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
                onClick={() => {
                  trackCTAClick('get_quote', 'hero');
                  const chatButton = document.querySelector('[aria-label="Open chat"]') as HTMLButtonElement;
                  if (chatButton) chatButton.click();
                }}
              >
                Get Free Estimate
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <BreadcrumbSchema items={breadcrumbItems} />
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container px-4 py-3">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </div>

      {/* Service Types Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1e3a8a]">Commercial Painting Services We Offer</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From office buildings to industrial facilities, we deliver exceptional results for every type of commercial property.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceTypes.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <service.icon className="h-12 w-12 text-[#4caf30] mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-muted-foreground">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-slate-50 dark:bg-slate-900">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1e3a8a]">Why Choose Danis Painting for Commercial Projects</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We understand that your business can't afford downtime or subpar results. Here's what sets us apart.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="flex gap-4">
                <Check className="h-6 w-6 text-[#0066cc] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1e3a8a]">Our Commercial Painting Process</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A proven system that delivers exceptional results while minimizing disruption to your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[#0066cc]">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Free Consultation</h3>
              <p className="text-muted-foreground">On-site assessment and detailed estimate with transparent pricing.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[#0066cc]">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Scheduling</h3>
              <p className="text-muted-foreground">Flexible timing around your business hours—evenings, weekends, or off-hours.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[#0066cc]">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Preparation</h3>
              <p className="text-muted-foreground">Thorough surface prep, repairs, and protection of your property and equipment.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-[#0066cc]">4</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Execution</h3>
              <p className="text-muted-foreground">Professional application with premium materials and meticulous attention to detail.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Locations Section */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1e3a8a]">Commercial Painting Services by Location</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Professional commercial painting services across Santa Barbara and Ventura counties. Click your city for local expertise.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { name: "Santa Barbara", slug: "santa-barbara" },
              { name: "Ventura", slug: "ventura" },
              { name: "Montecito", slug: "montecito" },
              { name: "Goleta", slug: "goleta" },
              { name: "Carpinteria", slug: "carpinteria" },
              { name: "Ojai", slug: "ojai" },
              { name: "Camarillo", slug: "camarillo" },
              { name: "Solvang", slug: "solvang" },
              { name: "Buellton", slug: "buellton" },
              { name: "Santa Ynez", slug: "santa-ynez" },
              { name: "Isla Vista", slug: "isla-vista" },
              { name: "Summerland", slug: "summerland" },
              { name: "Oxnard", slug: "oxnard" },
              { name: "Los Angeles", slug: "los-angeles" },
            ].map((location) => (
              <Link key={location.slug} href={`/service/commercial-painting/${location.slug}`}>
                <Card className="hover:shadow-lg hover:border-[#1e3a8a] transition-all cursor-pointer h-full">
                  <CardContent className="p-4 text-center">
                    <Building2 className="h-6 w-6 text-[#1e3a8a] mx-auto mb-2" />
                    <h3 className="font-semibold text-sm">{location.name}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Location-Specific Articles Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1e3a8a]">Commercial Painting Guides by Location</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Each city has unique challenges and opportunities. Read our location-specific commercial painting guides.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {locationArticles.map((article, index) => (
              <Link key={index} href={`/blog/${article.slug}`}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-xl font-semibold text-[#4caf30]">{article.city}</h3>
                      <ArrowRight className="h-5 w-5 text-[#4caf30] flex-shrink-0" />
                    </div>
                    <h4 className="text-lg font-medium mb-3">{article.title}</h4>
                    <p className="text-muted-foreground text-sm">{article.excerpt}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Commercial Property?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation estimate from Santa Barbara and Ventura's most trusted commercial painting contractor.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-[#4caf30] hover:bg-[#3d9426] text-white"
              onClick={() => {
                trackCTAClick('call_now', 'footer');
                window.location.href = "tel:805-403-8727";
              }}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call: 805-403-8727
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-transparent border-2 border-white text-white hover:bg-white/10"
              onClick={() => {
                trackCTAClick('get_quote', 'footer');
                const chatButton = document.querySelector('[aria-label="Open chat"]') as HTMLButtonElement;
                if (chatButton) chatButton.click();
              }}
            >
              Request Free Estimate
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section with visible accordion */}
      <FAQAccordion faqs={commercialPaintingFAQs} title="Commercial Painting FAQs" />
      
      {/* FAQ Schema for Google rich snippets */}
      <FAQSchema faqs={commercialPaintingFAQs} />
      <Footer />
    </div>
  );
}
